import React, { useEffect, useState } from 'react';
import {
  Box,
  SelectChangeEvent,
  MenuItem,
  Checkbox,
  Tooltip,
  styled,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import AppSelectBox from 'components/FormElements/AppSelectBox';

const useStyles = makeStyles()(() => ({
  placeholder: {
    color: '#999999',
  },
}));

const StyledBox = styled(Box)({
  overflow: 'hidden',
});

type Tranche = {
  _id: string;
  label: string | React.ReactNode;
};

type TrancheWithDisabledStatus = Tranche & {
  disabled: boolean;
};

type Props = {
  tranches: Array<Tranche>;
  handleChangeSelectedTranches: (trancheIds: Array<string>) => void;
  disabled?: boolean;
  value?: Array<string>;
  selectAllLabel?: string;
  getIsDisabled?: (tranche: Tranche) => boolean;
  getDisabledTooltip?: (tranche: Tranche) => string;
};

const TrancheMultiSelect: React.FC<Props> = ({
  tranches,
  handleChangeSelectedTranches,
  disabled = false,
  value,
  selectAllLabel = 'Select all',
  getIsDisabled,
  getDisabledTooltip,
}) => {
  const { classes } = useStyles();

  const [formState, setFormState] = useState({
    selectedTrancheIds: value || [],
    tranchesSelectAll: false,
  });

  useEffect(() => {
    if (Array.isArray(value)) {
      const enabledTranches = tranches.filter(
        (tranche) => !getIsDisabled?.(tranche),
      );
      const allEnabledSelected =
        value.length > 0 && value.length === enabledTranches.length;

      setFormState((prevState) => ({
        ...prevState,
        selectedTrancheIds: value,
        tranchesSelectAll: allEnabledSelected,
      }));
    }
  }, [value, tranches, getIsDisabled]);

  // This function handles the change event for the multiselect in the tranches dropdown.
  // It updates the state with the selected tranche ids.
  const handleTrancheMultiSelectChange = async (
    event: SelectChangeEvent<Array<string>>,
  ) => {
    const {
      target: { value },
    } = event;
    let trancheIds = formState.selectedTrancheIds;
    let tranchesSelectAll = formState.tranchesSelectAll;
    if (value.includes('all')) {
      // if already checked all, then uncheck all
      if (formState.tranchesSelectAll) {
        trancheIds = [];
      } else {
        // Only select tranches that are not disabled
        trancheIds = tranches
          .filter((fund) => !getIsDisabled?.(fund))
          .map((fund) => fund._id);
      }
      tranchesSelectAll = !tranchesSelectAll;
    } else {
      const enabledTranches = tranches.filter(
        (tranche) => !getIsDisabled?.(tranche),
      );
      if (value.length === enabledTranches.length) {
        if (!tranchesSelectAll) tranchesSelectAll = true;
      } else {
        tranchesSelectAll = false;
      }
      trancheIds = Array.isArray(value) ? value : [value];
    }
    // set the new state
    setFormState({
      ...formState,
      tranchesSelectAll,
      selectedTrancheIds: trancheIds,
    });
    handleChangeSelectedTranches(trancheIds);
  };

  // Map tranches to include disabled property when getIsDisabled is provided
  const tranchesWithDisabled = tranches.map((tranche) => ({
    ...tranche,
    disabled: getIsDisabled?.(tranche) ?? false,
  }));

  // Custom render function for rows when we need to add tooltips
  const renderRow =
    getIsDisabled && getDisabledTooltip
      ? (item: TrancheWithDisabledStatus, index: number) => {
          const isDisabled = getIsDisabled(item);
          const tooltipText = isDisabled ? getDisabledTooltip(item) : '';
          const isChecked =
            formState.selectedTrancheIds.findIndex((id) => id === item._id) >=
            0;

          const menuItem = (
            <MenuItem key={index} value={item._id} disabled={isDisabled}>
              <Checkbox color="primary" checked={isChecked} />
              {item.label}
            </MenuItem>
          );

          if (isDisabled && tooltipText) {
            return (
              <Tooltip key={index} title={tooltipText} arrow placement="left">
                <span>{menuItem}</span>
              </Tooltip>
            );
          }

          return menuItem;
        }
      : undefined;

  return (
    <>
      <AppSelectBox
        data={tranchesWithDisabled}
        valueKey="_id"
        displayEmpty
        name="trancheIds"
        labelKey="label"
        multiple
        variant="outlined"
        disabled={disabled}
        value={formState.selectedTrancheIds}
        isCheckedAll={formState.tranchesSelectAll}
        selectAllLabel={selectAllLabel}
        renderRow={renderRow}
        //@ts-expect-error
        onChange={handleTrancheMultiSelectChange}
        //@ts-expect-error
        renderValue={(selected: Array<string>) => (
          <StyledBox>
            {selected.length === 0 ? (
              <span className={classes.placeholder}>Select tranche(s)</span>
            ) : (
              selected.map((trancheId, index) => (
                <span key={trancheId}>
                  {index > 0 && ', '}
                  {tranches.find((val) => val?._id === trancheId)?.label}
                </span>
              ))
            )}
          </StyledBox>
        )}
      />
    </>
  );
};

export default TrancheMultiSelect;
